// src/components/Notes/SharedNoteViewer.tsx
import React, { useState, useEffect } from 'react';
import { X, Lock, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { EncryptionService } from '../../utils/encryption';

const encryption = new EncryptionService();

interface SharedNoteViewerProps {
  isOpen: boolean;
  noteId: string | null;
  userId: string;
  decryptedDEK: CryptoKey | null;
  onClose: () => void;
}

export const SharedNoteViewer: React.FC<SharedNoteViewerProps> = ({
  isOpen,
  noteId,
  userId,
  decryptedDEK,
  onClose,
}) => {
  const [note, setNote] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !noteId) return;

		const loadNote = async () => {
			setLoading(true);
			setError('');

			try {
				const response = await fetch(`/api/notes/shared-view?noteId=${noteId}`, {
					headers: { 'X-User-Id': userId }
				});

				const data = await response.json();

				if (!response.ok) {
					const errorMsg = data.error || '載入失敗';
					
					// ✅ UI 永遠顯示錯誤
					setError(errorMsg);
					
					// ✅ 只有非權限錯誤才彈 Toast
					if (response.status !== 403) {
						toast.error(errorMsg);
					}
					return;
				}

        const noteData = data.note;

        // ✅ 如果是自己的筆記，直接顯示
        if (noteData.isOwner) {
          // 用 DEK 解密 Note Key → 用 Note Key 解密內容
          if (decryptedDEK && noteData.encryptedNoteKey) {
            try {
              const noteKeyBuffer = await encryption.decryptNoteKeyWithDEK(
                noteData.encryptedNoteKey,
                decryptedDEK
              );
              const noteKey = await encryption.importNoteKey(noteKeyBuffer);

              const title = await encryption.decryptWithNoteKey(noteData.title, noteKey);
              const content = await encryption.decryptWithNoteKey(noteData.content, noteKey);
              const tags = await encryption.decryptWithNoteKey(noteData.tags, noteKey);
							const decryptedImages = noteData.images 
								? await encryption.decryptWithNoteKey(noteData.images, noteKey)
								: '[]';

              setNote({
                ...noteData,
                title,
                content,
                tags,
								images: decryptedImages,
                isDecrypted: true,
              });
              return;
            } catch (err) {
              console.warn('解密失敗:', err);
            }
          }
          // 如果解密失敗，顯示加密狀態
          setNote({
            ...noteData,
            isDecrypted: false,
          });
          return;
        }

        // ✅ 如果是別人分享的筆記
        if (noteData.targetNoteKey && decryptedDEK) {
          try {
            // 用 DEK 解密 Note Key
            const noteKeyBuffer = await encryption.decryptNoteKeyWithDEK(
              noteData.targetNoteKey,
              decryptedDEK
            );
            const noteKey = await encryption.importNoteKey(noteKeyBuffer);

            // 用 Note Key 解密內容
            const title = await encryption.decryptWithNoteKey(noteData.title, noteKey);
            const content = await encryption.decryptWithNoteKey(noteData.content, noteKey);
            const tags = await encryption.decryptWithNoteKey(noteData.tags, noteKey);
						const decryptedImages = noteData.images 
							? await encryption.decryptWithNoteKey(noteData.images, noteKey)
							: '[]';

            setNote({
              ...noteData,
              title,
              content,
              tags,
							images: decryptedImages,
              isDecrypted: true,
            });
          } catch (err) {
            console.warn('分享筆記解密失敗:', err);
            setNote({
              ...noteData,
              title: '🔒 加密筆記',
              content: '🔒 此筆記已加密，無法解密',
              tags: '[]',
              isDecrypted: false,
            });
          }
        } else {
          // 沒有 DEK 或沒有 targetNoteKey
          setNote({
            ...noteData,
            title: '🔒 加密筆記',
            content: '🔒 此筆記已加密，無法解密',
            tags: '[]',
            isDecrypted: false,
          });
        }

			} catch (err) {
				const errorMsg = err instanceof Error ? err.message : '載入失敗';
				setError(errorMsg);
				// ✅ catch 的錯誤通常是網路問題，需要彈 Toast
				toast.error(errorMsg);
			} finally {
				setLoading(false);
			}
		};

    loadNote();
  }, [isOpen, noteId, userId, decryptedDEK]);

  if (!isOpen) return null;

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'password': return '🔒';
      case 'todo': return '✅';
      default: return '📝';
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      '待處理': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      '進行中': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      '已完成': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      '暫擱置': 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400',
    };
    return <span className={`text-xs px-2 py-0.5 rounded-full ${colors[status] || 'bg-gray-100'}`}>{status}</span>;
  };

  // 安全解析 tags
  const parseTags = (tags: any) => {
    if (!tags) return [];
    if (Array.isArray(tags)) return tags;
    try {
      const parsed = JSON.parse(tags);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {}
    return [];
  };

  // 安全解析 images
  const parseImages = (images: string) => {
    if (!images) return [];
    try {
      const parsed = JSON.parse(images);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {}
    return [];
  };

  const tagList = parseTags(note?.tags);
  const imageList = parseImages(note?.images);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[150] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* 標題欄 */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-500" />
            檢視筆記
            {note?.isOwner ? (
              <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full">
                我的筆記
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-full">
                分享給我
              </span>
            )}
            {note?.permission === 'write' && (
              <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-full">
                ✏️ 可編輯
              </span>
            )}
            {note?.isDecrypted === false && (
              <span className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 rounded-full">
                ⚠️ 未解密
              </span>
            )}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* 內容 */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="text-gray-500 dark:text-gray-400">載入中...</div>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-500">{error}</div>
          ) : note ? (
            <div className="space-y-4">
              {/* 標題 */}
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xl">{getTypeIcon(note.type)}</span>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {note.title || '未命名筆記'}
                  </h2>
                </div>
                {note.owner_name && note.owner_name !== '自己' && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    👤 來自：{note.owner_name}
                  </p>
                )}
              </div>

              {/* 分類 + 日期 + 標籤 */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-600 dark:text-gray-300">
                  {note.category}
                </span>
                <span className="text-xs text-gray-400">{note.date}</span>
                {tagList.map((tag: string) => (
                  <span key={tag} className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full">
                    #{tag}
                  </span>
                ))}
              </div>

              {/* 密碼專屬 */}
              {note.type === 'password' && note.username && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300">👤 使用者名稱：</span>
                    <span className="text-gray-900 dark:text-white">{note.username}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300">🔒 密碼：</span>
                    <span className="text-gray-900 dark:text-white">{note.password}</span>
                  </div>
                </div>
              )}

              {/* 待辦專屬 */}
              {note.type === 'todo' && (
                <div className="flex items-center gap-2 flex-wrap">
                  {note.status && getStatusBadge(note.status)}
                  {note.due_date && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">📅 完成日期：{note.due_date}</span>
                  )}
                </div>
              )}

              {/* 內容 */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {note.content || '（無內容）'}
                </p>
              </div>

              {/* 圖片 */}
              {imageList.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    📷 圖片 ({imageList.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {imageList.map((img: string, index: number) => (
                      <button
                        key={index}
                        onClick={() => window.open(img, '_blank')}
                        className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        📷 圖片 {index + 1}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 唯讀提示 */}
              {!note.isOwner && note.permission === 'read' && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-sm text-blue-700 dark:text-blue-300 flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    📖 此為唯讀筆記，僅供查看
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              找不到筆記
            </div>
          )}
        </div>

        {/* 底部按鈕 */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            關閉
          </button>
        </div>
      </div>
    </div>
  );
};